import {
  SquarePenIcon,
  EmptyState,
  Flex,
  PlusIcon,
  RadioIcon,
} from '@buffer-mono/popcorn'

import { useCanPostOnAnyChannel } from '~publish/components/MainSidebar/components/ChannelsSection/useChannels'
import { NewPostComposerTrigger } from '~publish/components/NewPostComposerTrigger'
import { useHasOrganizationAction } from '~publish/contexts/AccountContext'

import {
  EmptyStateExploreCards,
  ExploreActionCard,
} from '../../../components/EmptyStates'
import { RETENTION_WINDOW_LABEL } from '../../../util/retention'

import styles from './ListeningForComments.module.css'

import type { JSX } from 'react'

type ListeningForCommentsProps = {
  isGBP?: boolean
  isPostView?: boolean
  /** Adds why an older post shows nothing, rather than "none yet". */
  isOlderThanRetentionWindow?: boolean
  /**
   * Optional notice rendered directly under the "listening" message, above the
   * "keep exploring" cards — e.g. the first-connect 30-day explainer.
   */
  notice?: React.ReactNode
}

export function ListeningForComments({
  isGBP,
  isPostView,
  isOlderThanRetentionWindow,
  notice,
}: ListeningForCommentsProps): JSX.Element {
  const itemLabel = isGBP ? 'reviews' : 'comments'
  const target = isPostView ? 'this post' : 'your posts'
  const canPostOnAnyChannel = useCanPostOnAnyChannel()
  const canConnectChannels = useHasOrganizationAction('manageChannels')

  return (
    <Flex direction="column" align="center" className={styles.container}>
      <Flex
        direction="column"
        align="center"
        gap="lg"
        className={styles.listeningHeader}
      >
        <EmptyState variant="success" className={styles.emptyState}>
          <EmptyState.Icon>
            <RadioIcon strokeWidth={1.5} />
          </EmptyState.Icon>
          <EmptyState.Heading>
            We&apos;re listening for new {itemLabel}
          </EmptyState.Heading>
          <EmptyState.Description>
            We&apos;ll let you know when someone{' '}
            {isGBP ? 'reviews' : 'comments on'} {target}.
            {isPostView && isOlderThanRetentionWindow
              ? ` It's more than a year old, and we only show ${itemLabel} from the past ${RETENTION_WINDOW_LABEL}.`
              : null}
          </EmptyState.Description>
        </EmptyState>
        {notice}
      </Flex>

      {(canPostOnAnyChannel || canConnectChannels) && (
        <EmptyStateExploreCards>
          {canPostOnAnyChannel && (
            <NewPostComposerTrigger cta="publish-community-listeningForComments-newPost-1">
              <ExploreActionCard
                icon={<SquarePenIcon size="small" />}
                title="Post something new"
                description="Use templates to create content that starts conversations. ✨ Pro tip: Ask a question"
              />
            </NewPostComposerTrigger>
          )}
          {canConnectChannels && (
            <ExploreActionCard
              icon={<PlusIcon size="small" />}
              title="Connect more channels"
              description="Manage conversations from all your channels from a single, distraction-free inbox."
              onClick={(): void => {
                window?.appshell?.actions?.openChannelStorefront({
                  cta: 'publish-community-listeningForComments-connectChannel-1',
                })
              }}
            />
          )}
        </EmptyStateExploreCards>
      )}
    </Flex>
  )
}
